import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError, downloadFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Printer, Download, RefreshCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { v: "single", l: "Single voucher" },
  { v: "range", l: "Range" },
  { v: "picked", l: "Pick from list" },
];

/** The exact number list a mode's inputs describe — mirrors the server's own reading of number / from+to /
 *  numbers (voucher_numbers_from_query() in server/voucher.php), so "Compute Sheets" never disagrees with
 *  what the server will actually build. */
function numbersFor(mode, { single, from, to, picked }) {
  if (mode === "single") {
    const n = parseInt(single, 10);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  if (mode === "range") {
    const f = parseInt(from, 10), t = parseInt(to, 10);
    if (!Number.isFinite(f) || !Number.isFinite(t) || f < 1 || t < 1) return [];
    const lo = Math.min(f, t), hi = Math.max(f, t);
    if (hi - lo > 999) return [];
    const out = [];
    for (let n = lo; n <= hi; n++) out.push(n);
    return out;
  }
  return [...picked].sort((a, b) => a - b);
}

function docNo(series, n) { return `${series} ${String(n).padStart(3, "0")}`; }

/** "Top: CV 101" / "Bottom: blank" / "Top: CV 105 (missing)" — a half is null when it was never
 *  requested (the untouched half of a reused sheet) or carries a doc_no with txn:null when it was
 *  requested but no such voucher exists (a real gap) — both print blank, only the second is a warning. */
function halfLabel(half) {
  if (half === null) return "blank";
  if (half.txn === null) return `${half.doc_no} (missing)`;
  return half.doc_no;
}

export default function VoucherPrint() {
  // A payroll-originated voucher's "Go to Print Vouchers" link (see PayrollPending.jsx) arrives with
  // ?series=&number= prefilled — this only ever fills the form; it never fetches a model or prints on its
  // own, so the deliberate-print-action rule holds regardless of how this page was reached.
  const location = useLocation();
  const prefill = useMemo(() => new URLSearchParams(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [series, setSeries] = useState(prefill.get("series") === "SA" ? "SA" : "CV");
  const [mode, setMode] = useState("single");
  const [single, setSingle] = useState(prefill.get("number") || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [available, setAvailable] = useState([]);   // [{number, doc_no, particulars}] for "pick from list"
  const [picked, setPicked] = useState(new Set());
  const [model, setModel] = useState(null);
  const [loadingModel, setLoadingModel] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  const numbers = useMemo(() => numbersFor(mode, { single, from, to, picked }), [mode, single, from, to, picked]);

  const clearPreview = () => {
    setModel(null);
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  };
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]); // eslint-disable-line

  // The list for "pick from list" comes straight from the journal — every voucher already listed there —
  // filtered down to this series' own doc numbers. Best-effort: a failure here only disables that one mode.
  useEffect(() => {
    setPicked(new Set());
    (async () => {
      try {
        const { data } = await api.get("/journal");
        const prefix = series + " ";
        const nums = data.rows
          .filter((r) => r.doc_no && r.doc_no.startsWith(prefix) && ["cv", "sa", "bill"].includes(r.entry_type))
          .map((r) => ({ number: parseInt(r.doc_no.slice(prefix.length), 10), doc_no: r.doc_no, particulars: r.particulars }))
          .filter((r) => Number.isFinite(r.number))
          .sort((a, b) => b.number - a.number);
        setAvailable(nums);
      } catch { setAvailable([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series]);

  const params = () => {
    if (mode === "single") return { series, number: numbers[0] };
    if (mode === "range") return { series, from: Math.min(parseInt(from, 10), parseInt(to, 10)), to: Math.max(parseInt(from, 10), parseInt(to, 10)) };
    return { series, numbers: numbers.join(",") };
  };

  const loadModel = async () => {
    if (numbers.length === 0) { toast.error("Enter at least one voucher number"); return null; }
    setLoadingModel(true);
    try {
      const { data } = await api.get("/vouchers/print", { params: params() });
      setModel(data);
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); return null; }
    finally { setLoadingModel(false); }
  };

  const loadPreview = async () => {
    const m = model || (await loadModel());
    if (!m) return;
    setLoadingPreview(true);
    try {
      const res = await api.get("/vouchers/print.pdf", { params: params(), responseType: "blob" });
      setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(res.data); });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoadingPreview(false); }
  };

  // Best-effort: a failed audit-log call must never block the actual print or download the user asked for.
  const logPrint = async () => { try { await api.post("/vouchers/print/log", { series, numbers }); } catch { /* best-effort */ } };

  // Wave review, minor: both actions used to call logPrint() before the PDF fetch even started, so a
  // failed fetch (network drop, a 500) still wrote a print_voucher audit row for a print that never
  // happened. Log only once the PDF has actually arrived — after downloadFile()/the GET below resolves,
  // never inside the catch block, so a request that threw never gets logged.
  const download = async () => {
    setBusy(true);
    try { await downloadFile("/vouchers/print.pdf", params(), `cash-vouchers_${series.toLowerCase()}.pdf`); await logPrint(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  // The same generated PDF the preview embeds is loaded into a throwaway hidden iframe and handed straight
  // to the browser's own print dialog — no second layout is ever built for this (see Task 16's design notes).
  const print = async () => {
    setBusy(true);
    try {
      const res = await api.get("/vouchers/print.pdf", { params: params(), responseType: "blob" });
      await logPrint();
      const url = URL.createObjectURL(res.data);
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* the viewer's own print button still works */ }
        setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
      };
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const togglePick = (n) => {
    setPicked((prev) => { const next = new Set(prev); if (next.has(n)) next.delete(n); else next.add(n); return next; });
    clearPreview();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-eyebrow">Stationery</div>
        <h1 className="font-heading text-4xl font-bold mt-1">Print Cash Vouchers</h1>
        <p className="text-sm text-muted mt-1">
          Two vouchers per A4 sheet — the odd number always prints on the top half, the even number on the
          bottom, so a sheet already carrying one can be fed back later for its partner.
        </p>
      </div>

      <Card className="p-5 border border-line space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Series</Label>
            <Select value={series} onValueChange={(v) => { setSeries(v); clearPreview(); }}>
              <SelectTrigger className="mt-1.5" data-testid="vp-series"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CV">CV · Cash Voucher</SelectItem>
                <SelectItem value="SA">SA · Suspense Advance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="label-eyebrow">Mode</Label>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {MODES.map((m) => (
                <Button key={m.v} type="button" size="sm" variant={mode === m.v ? "default" : "outline"}
                  className={mode === m.v ? "bg-ink text-white" : ""}
                  onClick={() => { setMode(m.v); clearPreview(); }} data-testid={`vp-mode-${m.v}`}>{m.l}</Button>
              ))}
            </div>
          </div>
        </div>

        {mode === "single" && (
          <div className="max-w-xs">
            <Label className="label-eyebrow">Voucher number</Label>
            <Input type="number" min="1" value={single} onChange={(e) => { setSingle(e.target.value); clearPreview(); }}
              placeholder="101" className="mt-1.5 font-mono" data-testid="vp-single" />
          </div>
        )}

        {mode === "range" && (
          <div className="flex gap-3 flex-wrap">
            <div className="max-w-xs">
              <Label className="label-eyebrow">From</Label>
              <Input type="number" min="1" value={from} onChange={(e) => { setFrom(e.target.value); clearPreview(); }}
                placeholder="101" className="mt-1.5 font-mono" data-testid="vp-from" />
            </div>
            <div className="max-w-xs">
              <Label className="label-eyebrow">To</Label>
              <Input type="number" min="1" value={to} onChange={(e) => { setTo(e.target.value); clearPreview(); }}
                placeholder="110" className="mt-1.5 font-mono" data-testid="vp-to" />
            </div>
          </div>
        )}

        {mode === "picked" && (
          <div>
            <Label className="label-eyebrow">Pick vouchers ({series})</Label>
            <div className="mt-1.5 max-h-56 overflow-y-auto border border-line rounded-md divide-y divide-line" data-testid="vp-picklist">
              {available.length === 0 && <div className="px-3 py-3 text-sm text-muted">No {series} vouchers found.</div>}
              {available.map((a) => (
                <label key={a.number} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-paper-alt">
                  <input type="checkbox" checked={picked.has(a.number)} onChange={() => togglePick(a.number)} data-testid={`vp-pick-${a.number}`} />
                  <span className="font-mono">{a.doc_no}</span>
                  <span className="text-muted truncate">{a.particulars}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap items-center">
          <Button onClick={loadModel} disabled={loadingModel || numbers.length === 0} data-testid="vp-compute" className="bg-ink text-white">
            <RefreshCcw size={14} className="mr-1.5" /> {loadingModel ? "Computing…" : "Compute Sheets"}
          </Button>
          {numbers.length > 0 && <span className="text-xs text-muted font-mono">{numbers.length} voucher number(s) requested</span>}
        </div>
      </Card>

      {model && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Vouchers Requested" v={model.requested.length} />
            <Stat label="Vouchers Found" v={model.found_count} />
            <Stat label="Sheets (A4)" v={model.sheet_count} strong />
          </div>

          {model.missing.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-md border border-cashout/40 bg-cashout/5 px-4 py-3 text-sm" data-testid="vp-missing">
              <AlertTriangle size={16} className="text-cashout shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Some requested numbers were not found and will print blank</div>
                <div className="text-muted mt-0.5 font-mono">{model.missing.map((n) => docNo(series, n)).join(", ")}</div>
              </div>
            </div>
          )}

          {model.requested.length === 1 && (
            <p className="text-sm text-muted" data-testid="vp-single-half-note">
              This voucher occupies the <strong>{model.sheets[0].top ? "top" : "bottom"}</strong> half of the sheet —
              the other half stays completely blank, ready for its partner later.
            </p>
          )}

          <Card className="border border-line divide-y divide-line overflow-hidden">
            {model.sheets.map((s) => (
              <div key={s.sheet} className="flex items-center justify-between px-4 py-2.5 text-sm font-mono flex-wrap gap-1" data-testid={`vp-sheet-${s.sheet}`}>
                <span className="text-muted">Sheet {s.sheet} of {model.sheet_count}</span>
                <span>Top: {halfLabel(s.top)} &nbsp;·&nbsp; Bottom: {halfLabel(s.bottom)}</span>
              </div>
            ))}
          </Card>

          <Card className="p-4 border border-line bg-paper-alt/50 text-xs text-muted space-y-1">
            <div className="label-eyebrow text-muted">Recommended printer settings</div>
            <div>A4 · Portrait · 100% scale (not "Fit to page") · Headers and footers off</div>
          </Card>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={loadPreview} variant="outline" disabled={loadingPreview} data-testid="vp-preview-btn">
              {loadingPreview ? "Loading preview…" : "Load Preview"}
            </Button>
            <Button onClick={download} disabled={busy} data-testid="vp-download-btn" className="bg-ink text-white">
              <Download size={14} className="mr-1.5" /> Download PDF
            </Button>
            <Button onClick={print} disabled={busy} data-testid="vp-print-btn" className="bg-ink text-white">
              <Printer size={14} className="mr-1.5" /> Print
            </Button>
          </div>

          {previewUrl && (
            <Card className="border border-line overflow-hidden" data-testid="vp-preview-frame">
              {/* The real generated PDF, embedded — never a second HTML rendition of the layout. */}
              <iframe title="Voucher sheet preview" src={previewUrl} className="w-full" style={{ height: "70vh" }} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, v, strong }) {
  return (
    <Card className="p-4 border border-line">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 font-mono tabular-nums ${strong ? "text-lg font-bold" : "text-sm font-semibold"}`}>{v}</div>
    </Card>
  );
}
